import * as React from "react";
import { useMemo } from "react";
import { Autocomplete, TextField, Chip } from "@mui/material";
import type { AutocompleteRenderInputParams } from "@mui/material";

import { userProfileTypeList } from "@utils/enum/profile";
import { useNotification } from "../../context";

export type RecipientKind = "center" | "segment" | "profile";

export interface RecipientOption {
  kind: RecipientKind;
  value: string;
  label: string;
}

export interface RecipientMultiSelectFieldProps {
  label?: string;
  placeholder?: string;
  value: RecipientOption[];
  onChange: (next: RecipientOption[]) => void;
  disabled?: boolean;
}

const buildRecipientOptions = (
  segmentsList: Array<{ value: string; label: string }>,
  centersList: Array<{ value: string; label: string }>
): RecipientOption[] => {
  const centers: RecipientOption[] = centersList.map((item) => ({
    kind: "center",
    value: item.value,
    label: item.label,
  }));

  const segments: RecipientOption[] = segmentsList.map((segment) => ({
    kind: "segment",
    value: segment.value,
    label: segment.label,
  }));

  const profiles: RecipientOption[] = userProfileTypeList.map((item) => ({
    kind: "profile",
    value: item.value,
    label: item.label,
  }));

  return [...centers, ...segments, ...profiles];
};

const groupLabelByKind: Record<RecipientKind, string> = {
  center: "Centros",
  segment: "Segmentos",
  profile: "Perfis",
};

const RecipientMultiSelectField: React.FC<RecipientMultiSelectFieldProps> = ({
  label = "Destinatário",
  placeholder = "Centro / Segmento / Perfil",
  value,
  onChange,
  disabled,
}) => {
  const { segments, centers } = useNotification();
  const options = useMemo(() => buildRecipientOptions(segments, centers), [
    segments,
    centers,
  ]);

  return (
    <Autocomplete<RecipientOption, true, false, false>
      multiple
      disableCloseOnSelect
      options={options}
      value={value}
      onChange={(_, nextValue) => onChange(nextValue)}
      groupBy={(option) => groupLabelByKind[option.kind]}
      getOptionLabel={(option) =>
        option.kind === "center"
          ? `${option.label} (${option.value})`
          : `${option.label}`
      }
      filterSelectedOptions
      disabled={disabled}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const props = getTagProps({ index });
          const colorByKind: Record<RecipientKind, string> = {
            center: "#1976d2", // azul
            segment: "#2e7d32", // verde
            profile: "#9c27b0", // roxo
          };

          return (
            <Chip
              {...props}
              key={`${option.kind}-${option.value}`}
              label={option.label}
              size="small"
              sx={{
                borderRadius: 1.5,
                bgcolor: "rgba(0,0,0,0.03)",
                border: `1px solid ${colorByKind[option.kind]}`,
                color: colorByKind[option.kind],
                "& .MuiChip-deleteIcon": {
                  color: colorByKind[option.kind],
                },
              }}
            />
          );
        })
      }
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant="outlined"
        />
      )}
      // filtro padrão do MUI já leva em conta label e valor,
      // mas podemos melhorar para buscar por label ou value case-insensitive
      filterOptions={(options, state) => {
        const search = state.inputValue.trim().toLowerCase();
        if (!search) return options;

        return options.filter((opt) => {
          const byLabel = opt.label.toLowerCase().includes(search);
          const byValue = opt.value.toLowerCase().includes(search);
          return byLabel || byValue;
        });
      }}
    />
  );
};

export default RecipientMultiSelectField;
